import axios from 'axios';

// Configuration
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Create axios instance with proper headers
const createRequest = (method, url, data = null) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const config = {
    method,
    url: `${API_BASE}${url}`,
    headers,
    timeout: 10000, // 10 second timeout
  };
  
  if (data) {
    config.data = data;
  }
  
  return axios(config);
};

// AI Chat Service - With robust error handling
const AIChatService = {
  
  // Check if backend is reachable
  checkBackendHealth: async () => {
    try {
      const response = await createRequest('GET', '/health');
      return {
        success: true,
        backend: response.data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: 'Backend not reachable',
        details: error.message
      };
    }
  },
  
  // Start new chat session
  startChat: async (groupId) => {
    try {
      console.log('🚀 Starting AI chat for group:', groupId);
      
      const response = await createRequest('POST', `/ai-chat/${groupId}/start`);
      
      if (response.data && response.data.success) {
        console.log('✅ AI chat started successfully');
        return response.data;
      } else {
        // Fallback if response structure is unexpected
        return this.getFallbackStartResponse(groupId);
      }
    } catch (error) {
      console.warn('⚠️ AI service unavailable, using fallback:', error.message);
      return this.getFallbackStartResponse(groupId);
    }
  },
  
  // Send message to AI
  sendMessage: async (groupId, message, sessionId) => {
    try {
      if (!message || message.trim().length === 0) {
        throw new Error('Message cannot be empty');
      }
      
      console.log('💬 Sending AI message:', message.substring(0, 50));
      
      const response = await createRequest('POST', `/ai-chat/${groupId}/message`, {
        message: message.trim(),
        sessionId: sessionId || `local_${Date.now()}`
      });
      
      if (response.data && response.data.success) {
        return response.data;
      } else {
        return this.getFallbackMessageResponse(message);
      }
    } catch (error) {
      console.warn('⚠️ AI message failed, using fallback:', error.message);
      return this.getFallbackMessageResponse(message);
    }
  },
  
  // Get quick suggestions
  getQuickSuggestions: async (groupId) => {
    try {
      console.log('🔍 Fetching AI suggestions for group:', groupId);
      
      const response = await createRequest('GET', `/ai-chat/${groupId}/suggestions`);
      
      if (response.data && response.data.success) {
        return response.data;
      } else {
        return this.getFallbackSuggestions();
      }
    } catch (error) {
      console.warn('⚠️ AI suggestions failed, using fallback:', error.message);
      return this.getFallbackSuggestions();
    }
  },
  
  // === FALLBACK RESPONSES (Always work even if backend is down) ===
  
  getFallbackStartResponse: (groupId) => ({
    success: true,
    sessionId: `fallback_${Date.now()}_${groupId}`,
    welcomeMessage: `👋 **Welcome to TripGenius AI!**\n\nI'm your travel planning assistant. While I'm connecting to enhanced features, I can still help you with:\n\n**Travel Planning Tips:**\n• Create balanced itineraries\n• Budget management strategies\n• Activity suggestions\n• Packing checklists\n• Group coordination advice\n\n**Ask me anything about your trip!**\n\n💡 *Using local mode - Enhanced AI features coming soon!*`,
    context: {
      groupId: groupId,
      mode: 'fallback',
      timestamp: new Date().toISOString()
    },
    fallback: true
  }),
  
  getFallbackMessageResponse: (message) => {
    const responses = [
      `**TripGenius AI:** I understand you want to know about "${message}".\n\n**Travel Tip:** Always check weather forecasts and local advisories before your trip. Pack layers for changing weather conditions!\n\n💡 *Try using the dashboard features for detailed planning!*`,
      `**TripGenius AI:** Regarding "${message}", here's my advice:\n• Plan activities based on group interests\n• Set a daily budget and track expenses\n• Keep digital copies of important documents\n• Share your itinerary with someone back home\n\n💡 *Check the Activity Planner for more ideas!*`,
      `**TripGenius AI:** Great question about travel planning!\n\nFor "${message}", consider:\n1. Researching destination thoroughly\n2. Creating a shared checklist\n3. Setting group expectations\n4. Planning flexible schedules\n5. Having emergency contacts ready\n\n💡 *Use the Checklist feature to stay organized!*`
    ];
    
    return {
      success: true,
      response: responses[Math.floor(Math.random() * responses.length)],
      sessionId: `fallback_${Date.now()}`,
      metadata: {
        messageType: 'fallback',
        timestamp: new Date().toISOString()
      },
      fallback: true
    };
  },
  
  getFallbackSuggestions: () => ({
    success: true,
    suggestions: [
      {
        id: 'itinerary_fallback',
        title: '📅 Plan Day',
        description: 'Daily schedule ideas',
        prompt: 'How to plan a daily itinerary?',
        icon: 'calendar'
      },
      {
        id: 'budget_fallback',
        title: '💰 Save Money',
        description: 'Budget tips',
        prompt: 'How to save money on travel?',
        icon: 'money'
      },
      {
        id: 'activities_fallback',
        title: '🎯 Group Fun',
        description: 'Activity ideas',
        prompt: 'What are fun group activities?',
        icon: 'activities'
      },
      {
        id: 'packing_fallback',
        title: '🧳 What to Pack',
        description: 'Essential items',
        prompt: 'What should I pack for the trip?',
        icon: 'luggage'
      },
      {
        id: 'safety_fallback',
        title: '🛡️ Stay Safe',
        description: 'Safety tips',
        prompt: 'Important safety tips for travelers',
        icon: 'shield'
      },
      {
        id: 'food_fallback',
        title: '🍽️ Local Food',
        description: 'Dining suggestions',
        prompt: 'How to find good local food?',
        icon: 'food'
      }
    ],
    fallback: true
  }),
  
  // Simple history endpoint
  getChatHistory: async (groupId) => ({
    success: true,
    sessions: [],
    message: 'Chat history available when AI service is fully connected',
    timestamp: new Date().toISOString()
  }),
  
  // Simple end session
  endChatSession: async (groupId, sessionId) => ({
    success: true,
    message: 'Session cleared locally',
    sessionId: sessionId,
    timestamp: new Date().toISOString()
  })
};

export default AIChatService;